import { format, formatDistanceToNow, isValid } from "date-fns";

/**
 * What a timestamp arrives as before anyone has checked it: a `Date` from a
 * picker, an epoch number from DynamoDB, an ISO string from GraphQL — or the
 * numeric string OpenSearch hands back for a DynamoDB `N`, which `new Date()`
 * rejects outright.
 */
export type Timestamp = Date | string | number | undefined | null;

/**
 * `format`, but it never throws.
 *
 * moment rendered "Invalid date" for input it could not read; date-fns throws a
 * RangeError instead, which unmounts the tree rather than showing an ugly cell.
 * These timestamps genuinely do arrive unreadable — the admin review queue read
 * "Invalid date" on every row for exactly that reason — so the sites that show
 * one straight from the API go through here and keep moment's visible behaviour.
 *
 * Format tokens are date-fns tokens, which are not moment's: `yyyy` not `YYYY`,
 * `dd`/`d` not `DD`/`D` (date-fns `D` is day-of-year), `aaa` for moment's `a`.
 */
export function formatTimestamp(value: Timestamp, pattern: string, fallback = "Invalid date"): string {
    const date = value instanceof Date ? value : new Date(value);
    return isValid(date) ? format(date, pattern) : fallback;
}

/**
 * `formatDistanceToNow` with a suffix, and the same no-throw guarantee.
 *
 * The wording is date-fns', not moment's — "about 2 hours ago" where `fromNow()`
 * said "2 hours ago".
 */
export function formatTimestampDistance(value: Timestamp, fallback = "Invalid date"): string {
    const date = value instanceof Date ? value : new Date(value);
    return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : fallback;
}
